import { useEffect } from "react";
import { supabaseRT } from "../realtimeClient";

// Hook genérico de realtime para a tabela `pedidos`. Inscreve UPDATEs/INSERTs
// que afetam o usuário (como cliente ou locador) e dispara `onChange` na
// hora em que o backend grava algo.
//
// Também aplica:
//   - Polling fallback (default 15s) — protege se realtime cair.
//   - Refresh ao voltar foco/visibility — útil em mobile que pausa sockets.
//
// Uso:
//   usePedidosRealtime({
//     userId: user?.id,
//     papel: "cliente" | "locador" | "ambos",
//     pedidoId: opcional — pra filtrar um pedido específico (checkout step enviado)
//     onChange: () => refetch(),
//     pollMs: 15000,
//   });

// Contador global pra gerar sufixo unico por instancia do hook.
// CRITICO: supabaseRT.channel(nome) RETORNA O CANAL EXISTENTE se ja houver
// um com o mesmo topico. Quando dois componentes vivos usavam o mesmo nome
// (ex.: hub global de notificacoes + TelaPedidoLocador, ambos em
// `pedidos_locador_<userId>`), o segundo recebia um canal JA INSCRITO e
// chamar .on() depois de subscribe() lanca:
//   "cannot add postgres_changes callbacks ... after subscribe()"
// -> ErrorBoundary derrubava o app inteiro (pior no painel do locador).
// Nome unico por instancia elimina o compartilhamento acidental.
let _instSeq = 0;

export const usePedidosRealtime = ({
  userId,
  papel = "cliente",
  pedidoId = null,
  onChange,
  pollMs = 15000,
}) => {
  useEffect(() => {
    if (!onChange) return;
    if (!userId && !pedidoId) return;

    // DEBOUNCE 400ms: rajada de eventos realtime (INSERT+UPDATE colados, ou
    // evento chegando junto do tick de poll) disparava N refetches completos
    // em sequencia — agora colapsa em 1. Realtime continua INSTANTANEO
    // (400ms e imperceptivel; o que pesava eram os fetches duplicados).
    let _debTimer = null;
    let _ultimoPayload = null;
    const onChangeDebounced = (payload) => {
      if (payload != null) _ultimoPayload = payload;
      if (_debTimer) clearTimeout(_debTimer);
      _debTimer = setTimeout(() => {
        _debTimer = null;
        const pl = _ultimoPayload;
        _ultimoPayload = null;
        try { onChange(pl); } catch {}
      }, 400);
    };

    // Sufixo unico por execucao do effect — nunca colide com canal vivo.
    const inst = ++_instSeq;
    const channelName = (pedidoId
      ? `pedidos_one_${pedidoId}`
      : `pedidos_${papel}_${userId}`) + `__i${inst}`;

    let filter;
    if (pedidoId) {
      filter = `id=eq.${pedidoId}`;
    } else if (papel === "cliente") {
      filter = `cliente_id=eq.${userId}`;
    } else if (papel === "locador") {
      filter = `locador_id=eq.${userId}`;
    } else {
      // 'ambos' — não dá pra fazer OR no filter do realtime, então
      // assina sem filter e checa no callback
      filter = null;
    }

    // BLINDAGEM: Realtime e ENHANCEMENT — o polling abaixo e o caminho
    // garantido. Qualquer erro do supabase-js aqui NAO pode derrubar o app.
    let channel = null;
    try {
      channel = supabaseRT.channel(channelName);
      channel.on(
        "postgres_changes",
        {
          event: "*", // INSERT + UPDATE + DELETE
          schema: "public",
          table: "pedidos",
          ...(filter ? { filter } : {}),
        },
        (payload) => {
          // Quando assina sem filter (papel='ambos'), filtra manualmente
          if (!filter && papel === "ambos") {
            const row = payload.new || payload.old;
            if (
              row?.cliente_id !== userId &&
              row?.locador_id !== userId
            ) return;
          }
          // Dispara callback — quem usa decide se refetch tudo ou só patch.
          onChangeDebounced(payload);
        }
      );
      channel.subscribe();
    } catch (e) {
      console.warn('[realtime] subscribe falhou (seguindo so com polling):', e?.message);
      try { if (channel) supabaseRT.removeChannel(channel); } catch {}
      channel = null;
    }

    // Polling fallback: a cada pollMs, dispara onChange (sem payload) pra
    // forçar refetch. Cobre cenários onde o socket WS caiu sem notificar.
    const pollInterval = pollMs > 0 ? setInterval(() => {
      // Pausa quando a aba está em segundo plano: sem isso o app seguia
      // buscando a cada poucos segundos com a tela apagada (bateria/rede).
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      onChangeDebounced(null);
    }, pollMs) : null;

    // Refresh quando aba volta a ficar visível (mobile/browser pausa sockets)
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        onChangeDebounced(null);
      }
    };
    document.addEventListener("visibilitychange", onVisible);

    // Mutação local (confirmar retirada, marcar devolvido, iniciar entrega…)
    // avisa na hora — sem esperar o próximo ciclo.
    const onEventoLocal = () => onChangeDebounced(null);
    window.addEventListener('toalugando:pedidos:mudou', onEventoLocal);

    return () => {
      try { if (channel) supabaseRT.removeChannel(channel); } catch {}
      if (pollInterval) clearInterval(pollInterval);
      if (_debTimer) clearTimeout(_debTimer);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener('toalugando:pedidos:mudou', onEventoLocal);
    };
  }, [userId, papel, pedidoId, onChange, pollMs]);
};
